/* global listUsersUrl ACCEPTED_ATTACHMENT_FILE_TYPES */

import React, { Fragment, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { format, parseISO } from 'date-fns';
import { Formik, Field, FieldArray } from 'formik';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import * as Yup from 'yup';

import DangerAlert from '../../partial/alerts/DangerAlert';
import ProcurementRevenuesTable from '../../procurement_plan/procurementRevenuesTable';
import ProcurementSponsorsTable from '../../procurement_plan/procurementSponsorsTable';
import ProcurementTicketingsTable from '../../procurement_plan/procurementTicketingsTable';
import SupplierContractReductionTable from '../SupplierContractReductionTable';

import SupplierContractTermExpensesTable from './SupplierContractTermExpensesTable';
import SupplierContractTermGoodsReceivedTable from './SupplierContractTermGoodsReceivedTable';

import { setUploadErrors } from 'Helpers/errors';
import { formatFigure, formatMoneyWithLocale } from 'Helpers/formatService';
import { cx } from 'Helpers/tabClassesBind';

import Button from 'CommonComponents/Button';
import CheckboxFormField from 'CommonComponents/CheckboxFormField';
import DatePickerField from 'CommonComponents/DatePickerField';
import EmptyState from 'CommonComponents/EmptyState';
import FormHorizontalSeparator from 'CommonComponents/FormHorizontalSeparator';
import FormSectionHeading from 'CommonComponents/FormSectionHeading';
import IconButton from 'CommonComponents/IconButton';
import NumberFormat from 'CommonComponents/LocalisedNumberFormat';
import ModalFooter from 'CommonComponents/ModalFooter';
import TextareaFormField from 'CommonComponents/TextareaFormField';
import TextFormField from 'CommonComponents/TextFormField';
import ToggleFormField from 'CommonComponents/ToggleFormField';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import CheckIcon from 'Assets/icons/check.svg';
import CirclePlusIcon from 'Assets/icons/circle-plus.svg';
import FilePlusIcon from 'Assets/icons/file-plus.svg';
import HourglassIcon from 'Assets/icons/hourglass.svg';
import PenSimpleIcon from 'Assets/icons/pen-simple.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const DEFAULT_PARTY = { name: '', position: '', role: '' };
const DEFAULT_MILESTONE = {
    description: '',
    due_date: null,
    forecast_due_date: null,
    completed_date: null,
    set_notification: false,
    notify_at: null,
};
const DEFAULT_PAYMENT = {
    description: '',
    due_date: null,
    forecast_due_date: null,
    amount: 0,
    set_notification: false,
    notify_at: null,
    paid_at: null,
};

const MILESTONE_TYPE_CASH = 'fixed/cash';
const MILESTONE_TYPE_VIK = 'fixed/vik';

// How far a milestone table's total may sit from its contract value and still count as
// matching it.
const PAYMENT_VARIANCE_TOLERANCE = 0.01;

// Above this, a saved cash/VIK split total counts as present and is used to apportion the
// contract value across the two milestone tables.
const MILESTONE_SPLIT_EPSILON = 0.0001;

const DEFAULT_PAYMENT_CASH = { ...DEFAULT_PAYMENT, milestone_type: MILESTONE_TYPE_CASH };
const DEFAULT_PAYMENT_VIK = { ...DEFAULT_PAYMENT, milestone_type: MILESTONE_TYPE_VIK };

/** Split API payments into cash vs VIK for two form tables. Legacy rows without milestone_type count as cash. */
function splitVariationPaymentsByMilestoneType(payments) {
    const cash = [];
    const vik = [];
    (payments || []).forEach((item) => {
        const row = { ...item, set_notification: !!item.notify_at };
        if (row.milestone_type === MILESTONE_TYPE_VIK) {
            vik.push(row);
        } else {
            cash.push(row);
        }
    });
    return { cash, vik };
}

/** Merge { cash, vik } form state back to the flat payments array the API expects. */
function mergeVariationPaymentsForSubmit(variationPaymentsObj) {
    if (!variationPaymentsObj || typeof variationPaymentsObj !== 'object' || Array.isArray(variationPaymentsObj)) {
        return [];
    }
    return Object.entries(variationPaymentsObj).map(([subContractId, group]) => {
        let payments = [];
        if (group && (Array.isArray(group.cash) || Array.isArray(group.vik))) {
            const cash = (group.cash || []).map((p) => ({ ...p, milestone_type: p.milestone_type || MILESTONE_TYPE_CASH }));
            const vik = (group.vik || []).map((p) => ({ ...p, milestone_type: p.milestone_type || MILESTONE_TYPE_VIK }));
            payments = [...cash, ...vik];
        } else if (Array.isArray(group)) {
            payments = group;
        }
        return {
            sub_contract_id: parseInt(subContractId, 10),
            payments,
        };
    });
}

/** Sum amounts for a variation group ({ cash, vik } or legacy flat array). */
function sumVariationGroupAmounts(group) {
    if (!group) {
        return 0;
    }
    if (Array.isArray(group)) {
        return group.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
    }
    const cashSum = (group.cash || []).reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
    const vikSum = (group.vik || []).reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
    return cashSum + vikSum;
}
const DEFAULT_PENALTY = { description: '' };
const DEFAULT_NOTE = { content: '' };

// Visible trigger for the otherwise hidden attachment file input. Clicking anywhere opens that
// input; dropping feeds the same callback. The content is pointer-transparent so dragging across
// the icon or the copy does not register as leaving the zone.
function AttachmentDropZone({ inputRef, onFilesSelected }) {
    const [isDragging, setIsDragging] = useState(false);

    const openFilePicker = () => inputRef.current?.click();

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={openFilePicker}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openFilePicker();
                }
            }}
            onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
            }}
            onDragLeave={(event) => {
                event.preventDefault();
                setIsDragging(false);
            }}
            onDrop={(event) => {
                event.preventDefault();
                setIsDragging(false);

                const files = Array.from(event.dataTransfer?.files || []);
                if (files.length) {
                    onFilesSelected(files);
                }
            }}
            className={classNames(
                'tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border-2 tw-border-dashed tw-px-6 tw-py-8 tw-text-center tw-transition-colors',
                isDragging ? 'tw-border-blue tw-bg-blue-10' : 'tw-border-grey tw-bg-background hover:tw-border-blue-50',
            )}
        >
            <div className="tw-pointer-events-none tw-flex tw-flex-col tw-items-center tw-gap-2">
                <FilePlusIcon className="tw-h-6 tw-w-6 tw-text-charcoal-50" aria-hidden="true" />
                <p className="tw-text-sm tw-text-charcoal-75">
                    {$t('Drag files here, or')} <span className="tw-font-semibold tw-text-blue">{$t('browse')}</span>
                </p>
                <p className="tw-text-xs tw-text-charcoal-50">{$t('PDF, Word or Excel')}</p>
            </div>
        </div>
    );
}

// Subtotals of the value groups the tab actually draws — a group with no rows renders nothing
// and contributes nothing. `trees` is the per-sub-contract shape, `legacyTree` the singular
// fallback used when the plural one is absent.
function collectRenderedGroupTotals(trees, legacyTree, rowsKey) {
    if (trees?.length > 0) {
        return trees.filter((tree) => tree?.[rowsKey]?.length).map((tree) => parseFloat(tree.total) || 0);
    }

    return legacyTree?.[rowsKey]?.length ? [parseFloat(legacyTree.total) || 0] : [];
}

const SupplierContractTermCreateSchema = Yup.object().shape({
    contact_name: Yup.string(),
    contact_email: Yup.string(),
    supply_scope: Yup.string(),
    start_date: Yup.date(),
    end_date: Yup.date().when('start_date', (start_date) => {
        if (start_date) {
            return Yup.date().min(start_date, $t('End Date must be after Start Date'));
        }
    }),
    owner_id: Yup.string().required($t('This value is required')),
    contract_closed: Yup.boolean(),
    contract_signed: Yup.boolean(),
    payments: Yup.array().of(
        Yup.object().shape({
            description: Yup.string().nullable(true),
            amount: Yup.number()
                .typeError($t('This value is required'))
                .required($t('This value is required'))
                .moreThan(0, $t('Amount must be greater than zero')),
            set_notification: Yup.bool(),
            notify_at: Yup.date().nullable(true),
            due_date: Yup.date().required($t('This value is required')),
            forecast_due_date: Yup.date().nullable(true),
            paid_at: Yup.date().nullable(true),
        }),
    ),
    milestones: Yup.array().of(
        Yup.object().shape({
            description: Yup.string().nullable(true),
            set_notification: Yup.bool(),
            notify_at: Yup.date().nullable(true),
            due_date: Yup.date().required($t('This value is required')),
            forecast_due_date: Yup.date().nullable(true),
            completed_date: Yup.date().nullable(true),
        }),
    ),
    penalties: Yup.array().of(
        Yup.object().shape({
            description: Yup.string().required($t('This value is required')),
        }),
    ),
    notes: Yup.array().of(
        Yup.object().shape({
            content: Yup.string().required($t('This value is required')),
        }),
    ),
    parties: Yup.array().of(
        Yup.object().shape({
            name: Yup.string().required($t('This value is required')),
            position: Yup.string().required($t('This value is required')),
            role: Yup.string().required($t('This value is required')),
        }),
    ),
    attachments: Yup.mixed(),
});

export default function SupplierContractTermCreateFrom(props) {
    const attachmentInputRef = useRef(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [errorAttachments, setErrorAttachments] = useState(null);
    const [defaultContactName, setDefaultContactName] = useState('');
    const [defaultContactEmail, setDefaultContactEmail] = useState('');
    const [defaultStartDate, setDefaultStartDate] = useState(null);
    const [defaultEndDate, setDefaultEndDate] = useState(null);
    const [shouldSwitchToValueTab, setShouldSwitchToValueTab] = useState(false);
    const [pendingQuantityChanges, setPendingQuantityChanges] = useState(null);
    const [pendingQuantityChangesMap, setPendingQuantityChangesMap] = useState({}); // Track changes per expense tree index
    // Receipts recorded in this modal session, keyed by committed expenditure id. Held here and
    // not in the Goods received tab: leaving the tab unmounts its panel, and a recorded delivery
    // that disappears reads as a failed one and gets recorded twice.
    const [goodsReceiptsRecorded, setGoodsReceiptsRecorded] = useState({});
    const [isSavingQuantities, setIsSavingQuantities] = useState(false);
    const [primaryButtonDisabled, setPrimaryButtonDisabled] = useState(false);
    // Derived, never held in state: the modal is portalled and only hidden with a class, so its
    // children do not unmount between openings and a useState initialiser would keep the first
    // contract's answer for the life of the page.
    const hasExpenseTrees = props.terms?.expenses_trees?.length > 0;
    const hasRevenueTrees = props.terms?.revenues_trees?.length > 0;
    const hasSponsorTrees = props.terms?.sponsors_trees?.length > 0;
    const hasTicketingTrees = props.terms?.ticketing_trees?.length > 0;
    const hasMultipleTrees = hasExpenseTrees || hasRevenueTrees || hasSponsorTrees || hasTicketingTrees;
    const hasVariationPayments = props.terms?.variation_payments?.length > 0;

    // The one family the Payment Milestones tab validates against: the first that has any trees.
    const resourceTrees =
        (hasExpenseTrees && props.terms.expenses_trees) ||
        (hasRevenueTrees && props.terms.revenues_trees) ||
        (hasSponsorTrees && props.terms.sponsors_trees) ||
        (hasTicketingTrees && props.terms.ticketing_trees) ||
        [];

    // Handle quantities updated - switch to Value tab and refresh data
    const handleQuantitiesUpdated = () => {
        // Set flag to switch to Value tab after data refresh
        setShouldSwitchToValueTab(true);

        // Call the parent callback to refresh contract terms
        if (props.onQuantitiesUpdated && typeof props.onQuantitiesUpdated === 'function') {
            props.onQuantitiesUpdated();
        }
    };

    // Handle redirection to Payment Milestones tab when quantities are reduced
    const handleRedirectToPaymentMilestones = (quantityChangeData, treeIndex = null) => {
        if (treeIndex !== null) {
            // Track changes per tree index
            setPendingQuantityChangesMap((prev) => ({
                ...prev,
                [treeIndex]: quantityChangeData,
            }));
        } else {
            // Legacy support for single tree
            setPendingQuantityChanges(quantityChangeData);
        }
        setTabIndex(2); // Switch to Payment Milestones tab (index 2)
    };

    // Save quantities and then payment milestones
    const saveQuantitiesAndMilestones = async (values, actions) => {
        const hasPendingChanges = props.terms?.expenses_trees?.length > 0 ? Object.keys(pendingQuantityChangesMap).length > 0 : !!pendingQuantityChanges;

        if (!hasPendingChanges) {
            return false;
        }

        setIsSavingQuantities(true);

        try {
            // First save the quantities
            const quantitiesUrl = props.updateQuantitiesUrl.replace('CONTRACT_ID', props.contract.id);

            if (props.terms?.expenses_trees?.length > 0) {
                // Save each expense tree's quantities
                const pendingChangesEntries = Object.entries(pendingQuantityChangesMap);
                for (const [treeIndex, changes] of pendingChangesEntries) {
                    const quantitiesData = {
                        originalExpensesTree: changes.updatedExpenseTree,
                        editedExpensesTree: changes.updatedExpenseTree,
                        editedAmounts: changes.editedAmounts,
                        auditLogs: changes.auditLogs,
                    };
                    await axios.post(quantitiesUrl, quantitiesData);
                }
            } else if (pendingQuantityChanges) {
                // Legacy support for single expense tree
                const quantitiesData = {
                    originalExpensesTree: pendingQuantityChanges.updatedExpenseTree,
                    editedExpensesTree: pendingQuantityChanges.updatedExpenseTree,
                    editedAmounts: pendingQuantityChanges.editedAmounts,
                    auditLogs: pendingQuantityChanges.auditLogs,
                };
                await axios.post(quantitiesUrl, quantitiesData);
            }

            // Then save the payment milestones
            const formData = new FormData();

            // Handle variation_payments separately to ensure proper JSON stringification
            const variationPaymentsValue = values.variation_payments;

            // Process all other fields first
            Object.entries(values).forEach(([key, value]) => {
                if (key === 'variation_payments') {
                    // Skip - we'll handle this separately
                    return;
                }

                if (['payments', 'milestones', 'penalties', 'notes', 'parties'].includes(key)) {
                    formData.append(key, JSON.stringify(value));
                } else if (key === 'attachments') {
                    value.forEach((attachment) => {
                        formData.append('attachments[]', attachment);
                    });
                } else if (key === 'contract_closed') {
                    formData.append(key, value ? '1' : '0');
                } else {
                    formData.append(key, value);
                }
            });

            // Handle variation_payments separately after processing other fields
            if (variationPaymentsValue !== undefined && variationPaymentsValue !== null) {
                // Ensure it's an object, not a string
                let variationPaymentsObj = variationPaymentsValue;

                // Handle if it's already a string (shouldn't happen, but defensive)
                if (typeof variationPaymentsValue === 'string') {
                    if (variationPaymentsValue === '[object Object]') {
                        console.error('[saveQuantitiesAndMilestones] variation_payments is "[object Object]" string - data lost, using empty object');
                        variationPaymentsObj = {};
                    } else {
                        try {
                            variationPaymentsObj = JSON.parse(variationPaymentsValue);
                        } catch (e) {
                            console.error('[saveQuantitiesAndMilestones] Failed to parse variation_payments string:', e);
                            variationPaymentsObj = {};
                        }
                    }
                }

                // Ensure it's a plain object (not array, not null)
                if (!variationPaymentsObj || typeof variationPaymentsObj !== 'object' || Array.isArray(variationPaymentsObj)) {
                    console.warn('[saveQuantitiesAndMilestones] variation_payments is not a valid object:', variationPaymentsObj);
                    variationPaymentsObj = {};
                }

                const variationPaymentsArray = mergeVariationPaymentsForSubmit(variationPaymentsObj);

                // Stringify and append to FormData
                const jsonString = JSON.stringify(variationPaymentsArray);
                formData.append('variation_payments', jsonString);
            } else {
                // If variation_payments is not set, send empty array
                formData.append('variation_payments', JSON.stringify([]));
            }

            const url = props.terms?.id
                ? props.updateUrl.replace('CONTRACT_ID', props.contract.id).replace('TERM_ID', props.terms.id)
                : props.storeUrl.replace('CONTRACT_ID', props.contract.id);

            const requestConfig = props.terms?.id
                ? { params: { _method: 'PUT' }, headers: { 'Content-Type': 'multipart/form-data' } }
                : { headers: { 'Content-Type': 'multipart/form-data' } };

            await axios.post(url, formData, requestConfig);

            // Clear pending changes and show success
            setPendingQuantityChanges(null);
            setPendingQuantityChangesMap({});
            actions.resetForm();
            props.onSuccess();

            return true;
        } catch (error) {
            console.error('Error saving quantities and milestones:', error);
            // A line with a budget change pending approval refuses quantity changes, saying why.
            const refusal = error?.response?.data?.errors?.pending_line_change?.[0];
            if (refusal) {
                alert(refusal);
            }
            return false;
        } finally {
            setIsSavingQuantities(false);
        }
    };

    // Auto-switch to Value tab when contract terms are updated after quantity changes
    useEffect(() => {
        // If we have contract terms data and the flag is set, switch to Value tab
        const hasExpensesTrees = props.terms?.expenses_trees?.length > 0;
        const hasExpensesTree = props.terms?.expenses_tree?.departments?.length > 0;
        if (shouldSwitchToValueTab && (hasExpensesTrees || hasExpensesTree)) {
            setTabIndex(1); // Switch to Value tab
            setShouldSwitchToValueTab(false); // Reset the flag
        }
    }, [props.terms?.expenses_trees, props.terms?.expenses_tree, shouldSwitchToValueTab]);

    useEffect(() => {
        if (props.terms?.contact_name) {
            setDefaultContactName(props.terms.contact_name);
        } else if (props.contract?.supplier?.supplier_contact?.name) {
            setDefaultContactName(props.contract.supplier.supplier_contact.name);
        }

        if (props.terms?.contact_email) {
            setDefaultContactEmail(props.terms.contact_email);
        } else if (props.contract?.supplier?.supplier_contact?.email) {
            setDefaultContactEmail(props.contract.supplier.supplier_contact.email);
        }

        if (!props.terms && props.contract?.purchase_orders?.length) {
            const purchaseOrder = props.contract.purchase_orders[0];
            setDefaultStartDate(purchaseOrder.contract_started_at ? format(parseISO(purchaseOrder.contract_started_at), 'yyyy-MM-dd') : null);
            setDefaultEndDate(purchaseOrder.contract_ended_at ? format(parseISO(purchaseOrder.contract_ended_at), 'yyyy-MM-dd') : null);
        }
    }, [props.terms, props.contract]);

    // The modal is portalled and only hidden with a class, so this component survives being
    // closed and reopened on another contract. Receipts recorded against the previous one have to
    // be dropped explicitly or they would be read as that contract's figures.
    useEffect(() => {
        setGoodsReceiptsRecorded({});
    }, [props.terms?.id, props.contract?.id]);

    return (
        <Formik
            enableReinitialize
            initialValues={{
                contact_name: defaultContactName,
                contact_email: defaultContactEmail,
                supply_scope: props.terms?.supply_scope || '',
                start_date: props.terms?.start_date || defaultStartDate || '',
                end_date: props.terms?.end_date || defaultEndDate || '',
                owner_id: props.terms?.owner_id || '',
                contract_closed: props.contract?.contract_closed || 0,
                contract_signed: props.contract?.contract_signed || 0,
                payments: props.terms?.payments?.map((item) => ({ ...item, set_notification: !!item.notify_at })) || [],
                variation_payments: (() => {
                    const variationPaymentsMap = {};
                    if (props.terms?.variation_payments?.length > 0) {
                        props.terms.variation_payments.forEach((vp) => {
                            const raw =
                                vp.payments?.map((item) => ({
                                    ...item,
                                    set_notification: !!item.notify_at,
                                })) || [];
                            variationPaymentsMap[vp.sub_contract_id] = splitVariationPaymentsByMilestoneType(raw);
                        });
                    }
                    (props.terms?.expenses_trees || []).forEach((tree) => {
                        const id = tree.sub_contract_id;
                        if (variationPaymentsMap[id] === undefined) {
                            variationPaymentsMap[id] = { cash: [], vik: [] };
                        }
                    });
                    return variationPaymentsMap;
                })(),
                milestones: props.terms?.milestones?.map((item) => ({ ...item, set_notification: !!item.notify_at })) || [],
                penalties: props.terms?.penalties || [],
                notes: props.terms?.notes || [],
                parties: props.terms?.parties || [],
                attachments: [],
            }}
            validationSchema={SupplierContractTermCreateSchema}
            onSubmit={async (values, actions) => {
                setPrimaryButtonDisabled(true);

                // Check if we have pending quantity changes and we're on Payment Milestones tab
                const hasPendingChanges =
                    props.terms?.expenses_trees?.length > 0 ? Object.keys(pendingQuantityChangesMap).length > 0 : !!pendingQuantityChanges;

                if (hasPendingChanges && tabIndex === 2) {
                    // Validate variation_payments if using multiple tables
                    if (hasMultipleTrees && hasVariationPayments) {
                        let validationErrors = {};
                        let hasValidationError = false;

                        resourceTrees.forEach((resourceTree, treeIndex) => {
                            const subContractId = resourceTree.sub_contract_id;
                            const pendingChanges = pendingQuantityChangesMap[treeIndex] || null;
                            const treeContractValue = pendingChanges ? pendingChanges.newTotal : resourceTree?.total || 0;

                            const subContractGroup = values.variation_payments?.[subContractId];
                            const subContractTotalPayments = sumVariationGroupAmounts(subContractGroup);

                            if (Math.abs(treeContractValue - subContractTotalPayments) > PAYMENT_VARIANCE_TOLERANCE) {
                                validationErrors[`variation_payments.${subContractId}`] = $t('Payment milestones total must match the new contract value');
                                hasValidationError = true;
                            }
                        });

                        if (hasValidationError) {
                            actions.setErrors(validationErrors);
                            actions.setSubmitting(false);
                            setPrimaryButtonDisabled(false);
                            return;
                        }
                    } else {
                        // Legacy validation for single payments table
                        // Calculate total contract value from all expense trees
                        let newContractValue = props.contract?.cashflow?.total || 0;

                        if (props.terms?.expenses_trees?.length > 0) {
                            newContractValue = props.terms.expenses_trees.reduce((sum, tree, index) => {
                                const pendingChanges = pendingQuantityChangesMap[index];
                                if (pendingChanges) {
                                    return sum + (pendingChanges.newTotal || 0);
                                }
                                return sum + (tree?.total || 0);
                            }, 0);
                        } else if (pendingQuantityChanges) {
                            newContractValue = pendingQuantityChanges.newTotal;
                        }

                        // Validate that payment milestones total matches the new contract value
                        const currentTotalPayments = values.payments?.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0) || 0;

                        if (Math.abs(currentTotalPayments - newContractValue) > PAYMENT_VARIANCE_TOLERANCE) {
                            actions.setErrors({
                                payments: $t('Payment milestones total must match the new contract value'),
                            });
                            actions.setSubmitting(false);
                            setPrimaryButtonDisabled(false);
                            return;
                        }
                    }

                    // Save quantities first, then payment milestones
                    const success = await saveQuantitiesAndMilestones(values, actions);
                    if (success) {
                        actions.setSubmitting(false);
                        setPrimaryButtonDisabled(false);
                    } else {
                        actions.setSubmitting(false);
                        setPrimaryButtonDisabled(false);
                    }
                    return;
                }

                // Check if we're on Value tab and have pending quantity changes
                if (hasPendingChanges && tabIndex === 1) {
                    // Don't allow saving from Value tab if payment milestones haven't been updated
                    actions.setErrors({
                        general: $t('Please update payment milestones to match the new contract value before saving'),
                    });
                    actions.setSubmitting(false);
                    setPrimaryButtonDisabled(false);
                    return;
                }

                // Normal form submission
                const formData = new FormData();
                setErrorAttachments(null);

                // Handle variation_payments separately to ensure proper handling
                const variationPaymentsValue = values.variation_payments;

                // Process all other fields first
                Object.entries(values).forEach(([key, value]) => {
                    if (key === 'variation_payments') {
                        // Skip - we'll handle this separately
                        return;
                    }

                    if (['payments', 'milestones', 'penalties', 'notes', 'parties'].includes(key)) {
                        formData.append(key, JSON.stringify(value));
                    } else if (key === 'attachments') {
                        value.forEach((attachment) => {
                            formData.append('attachments[]', attachment);
                        });
                    } else if (key === 'contract_closed' || key === 'contract_signed') {
                        formData.append(key, value ? '1' : '0');
                    } else {
                        formData.append(key, value);
                    }
                });

                // Handle variation_payments separately after processing other fields
                if (variationPaymentsValue !== undefined && variationPaymentsValue !== null) {
                    // Ensure it's an object, not a string
                    let variationPaymentsObj = variationPaymentsValue;

                    // Handle if it's already a string (shouldn't happen, but defensive)
                    if (typeof variationPaymentsValue === 'string') {
                        if (variationPaymentsValue === '[object Object]') {
                            console.error('variation_payments is "[object Object]" string - data lost, using empty object');
                            variationPaymentsObj = {};
                        } else {
                            try {
                                variationPaymentsObj = JSON.parse(variationPaymentsValue);
                            } catch (e) {
                                console.error('Failed to parse variation_payments string:', e);
                                variationPaymentsObj = {};
                            }
                        }
                    }

                    // Ensure it's a plain object (not array, not null)
                    if (!variationPaymentsObj || typeof variationPaymentsObj !== 'object' || Array.isArray(variationPaymentsObj)) {
                        console.warn('variation_payments is not a valid object:', variationPaymentsObj);
                        variationPaymentsObj = {};
                    }

                    const variationPaymentsArray = mergeVariationPaymentsForSubmit(variationPaymentsObj);

                    // Stringify and append to FormData
                    const jsonString = JSON.stringify(variationPaymentsArray);
                    formData.append('variation_payments', jsonString);
                } else {
                    // If variation_payments is not set, send empty array
                    formData.append('variation_payments', JSON.stringify([]));
                }

                if (props.terms?.id) {
                    const url = props.updateUrl.replace('CONTRACT_ID', props.contract.id).replace('TERM_ID', props.terms.id);

                    axios
                        .post(url, formData, {
                            params: { _method: 'PUT' }, // This is a workaround to be able to send multipart/form-data as put request
                            headers: { 'Content-Type': 'multipart/form-data' },
                        })
                        .then((res) => {
                            actions.resetForm();
                            props.onSuccess();
                        })
                        .catch((e) => {
                            setUploadErrors(e, 'attachments.0', 'attachments', setErrorAttachments, false);
                            e?.response?.data?.errors && actions.setErrors(e.response.data.errors);
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                            setPrimaryButtonDisabled(false);
                        });
                } else {
                    const url = props.storeUrl.replace('CONTRACT_ID', props.contract.id);

                    axios
                        .post(url, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                        })
                        .then((res) => {
                            props.onSuccess();
                            actions.resetForm();
                        })
                        .catch((e) => {
                            setUploadErrors(e, 'attachments.0', 'attachments', setErrorAttachments, false);
                            if (e.response?.data?.errors) {
                                actions.setErrors(e.response.data.errors);
                            }
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                            setPrimaryButtonDisabled(false);
                        });
                }
            }}
        >
            {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue, setFieldTouched }) => {
                // Calculate totals based on current values
                // For multiple expense trees, sum up all the totals
                let contractValue = props.contract?.cashflow?.total || 0;

                if (props.terms?.expenses_trees?.length > 0) {
                    // Calculate total from all expense trees, using pending changes if available
                    contractValue = props.terms.expenses_trees.reduce((sum, tree, index) => {
                        const pendingChanges = pendingQuantityChangesMap[index];
                        if (pendingChanges) {
                            return sum + (pendingChanges.newTotal || 0);
                        }
                        return sum + (tree?.total || 0);
                    }, 0);
                } else if (pendingQuantityChanges) {
                    // Legacy support for single expense tree
                    contractValue = pendingQuantityChanges.newTotal;
                }

                // Subtotal of every value table the Value tab actually draws, in the order it draws
                // them. Deliberately not `contractValue` above: that one aggregates the expenses
                // trees alone and falls back to the contract cashflow total, so on a ticketing,
                // revenue or sponsor contract it never sees the groups that are on screen.
                //
                // Each table hides itself when its own rows are empty, so the conditions here mirror
                // the render conditions rather than the presence of the key — otherwise the total
                // would count groups the user cannot see. Expenses honour a pending quantity edit
                // the same way the table does, so the figure tracks the tab while quantities change.
                const renderedExpensesTotals = [];

                if (props.terms?.expenses_trees?.length > 0) {
                    props.terms.expenses_trees.forEach((expensesTree, treeIndex) => {
                        const pendingChanges = pendingQuantityChangesMap[treeIndex] || null;
                        const renderedTree = pendingChanges ? pendingChanges.updatedExpenseTree : expensesTree;

                        if (renderedTree?.departments?.length) {
                            renderedExpensesTotals.push(parseFloat(pendingChanges ? pendingChanges.newTotal : expensesTree?.total) || 0);
                        }
                    });
                } else {
                    const renderedTree = pendingQuantityChanges ? pendingQuantityChanges.updatedExpenseTree : props.terms?.expenses_tree;

                    if (renderedTree?.departments?.length) {
                        renderedExpensesTotals.push(
                            parseFloat(pendingQuantityChanges ? pendingQuantityChanges.newTotal : props.terms?.expenses_tree?.total) || 0,
                        );
                    }
                }

                const renderedValueTotals = [
                    ...renderedExpensesTotals,
                    ...collectRenderedGroupTotals(props.terms?.sponsors_trees, props.terms?.sponsors_tree, 'sponsors'),
                    ...collectRenderedGroupTotals(props.terms?.revenues_trees, props.terms?.revenues_tree, 'revenues'),
                    ...collectRenderedGroupTotals(props.terms?.ticketing_trees, props.terms?.ticketing_tree, 'ticketings'),
                ];
                const overallValueTotal = renderedValueTotals.reduce((sum, total) => sum + total, 0);

                // Calculate totals for variation_payments (multiple tables)
                let allVariationPaymentsValid = true;
                let allVariationPaymentsTotal = 0;

                if (hasMultipleTrees && hasVariationPayments) {
                    // Validate each variation_payments table separately
                    resourceTrees.forEach((resourceTree, treeIndex) => {
                        const subContractId = resourceTree.sub_contract_id;
                        const pendingChanges = pendingQuantityChangesMap[treeIndex] || null;
                        const treeContractValue = pendingChanges ? pendingChanges.newTotal : resourceTree?.total || 0;

                        const subContractGroup = values.variation_payments?.[subContractId];
                        const subContractTotalPayments = sumVariationGroupAmounts(subContractGroup);

                        const cashRows = subContractGroup?.cash || [];
                        const vikRows = subContractGroup?.vik || [];
                        const hasInvalidAmounts = [...cashRows, ...vikRows].some((p) => !(parseFloat(p?.amount) > 0));
                        const variance = Math.abs(treeContractValue - subContractTotalPayments);

                        if (hasInvalidAmounts || variance > PAYMENT_VARIANCE_TOLERANCE) {
                            allVariationPaymentsValid = false;
                        }

                        allVariationPaymentsTotal += subContractTotalPayments;
                    });
                }

                // Legacy support for single payments table
                const currentTotalPayments = values.payments?.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0) || 0;
                const currentVariance = contractValue - currentTotalPayments;
                const hasInvalidAmounts = (values.payments || []).some((p) => !(parseFloat(p?.amount) > 0));

                // Check if payment milestones match the new contract value when there are pending changes
                const isPaymentMilestonesValid = () => {
                    // If using variation_payments, validate each table separately
                    if (hasMultipleTrees && hasVariationPayments) {
                        return allVariationPaymentsValid;
                    }

                    // Legacy validation for single payments table
                    if (hasInvalidAmounts) {
                        return false;
                    }

                    const hasPendingChanges =
                        props.terms?.expenses_trees?.length > 0 ? Object.keys(pendingQuantityChangesMap).length > 0 : !!pendingQuantityChanges;

                    if (!hasPendingChanges) {
                        return Math.abs(currentVariance) <= PAYMENT_VARIANCE_TOLERANCE;
                    }

                    return Math.abs(currentTotalPayments - contractValue) <= PAYMENT_VARIANCE_TOLERANCE;
                };

                const shouldDisableSave = !isPaymentMilestonesValid();

                // Handle save button click with validation
                const handleSaveClick = (e) => {
                    if (shouldDisableSave) {
                        e.preventDefault();
                        e.stopPropagation();

                        // Show an informative alert
                        if (hasInvalidAmounts) {
                            alert($t('Each payment milestone must have an amount greater than zero.'));
                        } else {
                            const hasPendingChanges =
                                props.terms?.expenses_trees?.length > 0 ? Object.keys(pendingQuantityChangesMap).length > 0 : !!pendingQuantityChanges;
                            alert(
                                hasPendingChanges
                                    ? $t('Payment milestones total must equal to the new contract value in order to save changes.')
                                    : $t('Payment milestones total must equal to the contract value in order to save changes.'),
                            );
                        }
                        return false;
                    }
                    return handleSubmit(e);
                };

                return (
                    <>
                        {!!Object.keys(errors).length &&
                            !!Object.keys(touched).length &&
                            Object.keys(errors).every((key) => Object.keys(touched).includes(key)) && (
                                <div className="tw-text-red-500 tw-text-sm">Something is invalid in this tab or the other tabs.</div>
                            )}
                        {errors.supplier_contract_id && <div className="tw-text-red-500 tw-text-sm">{errors.supplier_contract_id}</div>}

                        <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
                            <TabList className="tab__list">
                                <Tab className={cx({ selected: tabIndex == 0, default: tabIndex != 0 })} selectedClassName="">
                                    {$t('General')}
                                </Tab>
                                <Tab className={cx({ selected: tabIndex == 1, default: tabIndex != 1 })} selectedClassName="">
                                    {$t('Value')}
                                </Tab>
                                <Tab className={cx({ selected: tabIndex == 2, default: tabIndex != 2 })} selectedClassName="">
                                    {$t('Payment Milestones')}
                                </Tab>
                                <Tab className={cx({ selected: tabIndex == 3, default: tabIndex != 3 })} selectedClassName="">
                                    {$t('Other Milestones')}
                                </Tab>
                                <Tab className={cx({ selected: tabIndex == 4, default: tabIndex != 4 })} selectedClassName="">
                                    {$t('Goods received')}
                                </Tab>
                                <Tab className={cx({ selected: tabIndex == 5, default: tabIndex != 5 })} selectedClassName="">
                                    {$t('Supplier Notes')}
                                </Tab>
                            </TabList>

                            <TabPanel>
                                <div className="modal__form-container tw-h-[calc(70vh-2.5rem)] 2xl:tw-h-[calc(50vh-2.5rem)] tw-overflow-y-scroll">
                                    {/* Three sections, in the order a contract is read: who the supplier is,
                                        what was agreed with them, then the scope and its paperwork. */}
                                    <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                                        <FormSectionHeading heading={$t('Supplier')}></FormSectionHeading>
                                        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                <label className="text-input__label">{$t('Company name')}</label>
                                                <input type="text" className="tw-border-0 text-input" value={props.contract?.supplier?.name || ''} disabled />
                                            </div>
                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                <label className="text-input__label">{$t('Supplier code')}</label>
                                                <input type="text" className="tw-border-0 text-input" value={props.contract?.supplier?.code || ''} disabled />
                                            </div>
                                            <TextFormField label={$t('Contact name')} type="text" name="contact_name"></TextFormField>
                                            <TextFormField label={$t('Contact email')} type="text" name="contact_email"></TextFormField>
                                        </div>
                                    </div>

                                    <FormHorizontalSeparator></FormHorizontalSeparator>

                                    <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                                        <FormSectionHeading heading={$t('Contract')}></FormSectionHeading>
                                        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                <label className="text-input__label">{$t('Owner')}</label>
                                                <UserSelectorInput
                                                    selectedId={values.owner_id}
                                                    selectedUser={props.terms?.owner}
                                                    usersList={props.users}
                                                    onChange={(value) => setFieldValue('owner_id', value)}
                                                    fetchUrl={listUsersUrl}
                                                />
                                                {errors.owner_id && touched.owner_id ? (
                                                    <div className="tw-text-red-500 tw-text-sm">{errors.owner_id}</div>
                                                ) : null}
                                            </div>
                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                <label className="text-input__label">{$t('Value')}</label>
                                                <NumberFormat
                                                    thousandSeparator={true}
                                                    inputMode="numeric"
                                                    className="tw-border-0 text-input"
                                                    value={props.contract?.cashflow?.total || 0}
                                                    disabled
                                                />
                                            </div>
                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                <label className="text-input__label">{$t('Contract start date')}</label>
                                                <DatePickerField
                                                    name="start_date"
                                                    className={
                                                        errors.start_date && touched.start_date
                                                            ? 'tw-border tw-border-red-500 text-input'
                                                            : 'tw-border-0 text-input'
                                                    }
                                                    value={values.start_date}
                                                    valueFormat="yyyy-MM-dd"
                                                    dateFormat="yyyy-MM-dd"
                                                    onChange={setFieldValue}
                                                />
                                                {errors.start_date && touched.start_date && (
                                                    <div className="tw-text-red-500 tw-text-sm">{errors.start_date}</div>
                                                )}
                                            </div>
                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                <label className="text-input__label">{$t('Contract end date')}</label>
                                                <DatePickerField
                                                    name="end_date"
                                                    className={
                                                        errors.end_date && touched.end_date
                                                            ? 'tw-border tw-border-red-500 text-input'
                                                            : 'tw-border-0 text-input'
                                                    }
                                                    value={values.end_date}
                                                    valueFormat="yyyy-MM-dd"
                                                    dateFormat="yyyy-MM-dd"
                                                    onChange={setFieldValue}
                                                />
                                                {errors.end_date && touched.end_date && <div className="tw-text-red-500 tw-text-sm">{errors.end_date}</div>}
                                            </div>
                                            <div className="tw-col-span-2 tw-flex tw-items-center tw-gap-8">
                                                <CheckboxFormField label={$t('Contract signed')} type="checkbox" name="contract_signed" id="contract_signed" />
                                                <CheckboxFormField label={$t('Contract closed')} type="checkbox" name="contract_closed" id="contract_closed" />
                                            </div>
                                        </div>
                                    </div>

                                    <FormHorizontalSeparator></FormHorizontalSeparator>

                                    <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                                        <FormSectionHeading heading={$t('Scope & attachments')}></FormSectionHeading>
                                        <TextareaFormField label={$t('Scope of supply')} as="textarea" type="text" name="supply_scope"></TextareaFormField>
                                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                            <input
                                                ref={attachmentInputRef}
                                                type="file"
                                                className={
                                                    errors.attachments && touched.attachments
                                                        ? 'tw-border tw-border-red-500 file-input'
                                                        : 'tw-border-0 file-input'
                                                }
                                                name="attachments"
                                                multiple
                                                hidden
                                                accept={ACCEPTED_ATTACHMENT_FILE_TYPES}
                                                onChange={(event) => {
                                                    setFieldValue('attachments', Array.from(event.currentTarget.files));
                                                }}
                                            />
                                            <AttachmentDropZone
                                                inputRef={attachmentInputRef}
                                                onFilesSelected={(files) => setFieldValue('attachments', files)}
                                            />
                                            {/* Files picked or dropped are only staged here — they upload when the form
                                                is saved, so without this list the panel looks unchanged until then. */}
                                            {!!values.attachments?.length && (
                                                <div className="tw-flex tw-flex-col tw-gap-1 tw-pt-1">
                                                    <span className="tw-text-xs tw-font-semibold tw-text-charcoal-75">{$t('Selected files')}</span>
                                                    <ul className="tw-flex tw-flex-col tw-gap-1">
                                                        {values.attachments.map((file, index) => (
                                                            <li key={`${file.name}-${index}`} className="tw-truncate tw-text-sm tw-text-charcoal-75">
                                                                {file.name}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {errors.attachments && touched.attachments && (
                                                <div className="tw-text-red-500 tw-text-sm">{errors.attachments}</div>
                                            )}
                                            {!!errorAttachments && _.isArray(errorAttachments) && (
                                                <>
                                                    {errorAttachments.map((e, i) => {
                                                        return <DangerAlert key={i} message={e} />;
                                                    })}
                                                </>
                                            )}
                                        </div>
                                        {!!props.terms?.attachments?.length && (
                                            <Attachments
                                                attachments={props.terms.attachments}
                                                onDelete={props.onDeleteAttachment}
                                                onDownload={props.onDownloadAttachment}
                                            />
                                        )}
                                    </div>
                                </div>
                            </TabPanel>
                            <TabPanel>
                                <div className="modal__form-container tw-h-[calc(70vh-2.5rem)] 2xl:tw-h-[calc(50vh-2.5rem)] tw-overflow-y-scroll">
                                    <div className="tw-flex tw-flex-col tw-gap-8 tw-h-full tw-max-h-full">
                                        {/* Render tables for multiple expense trees */}
                                        {props.terms?.expenses_trees?.length > 0
                                            ? props.terms.expenses_trees.map((expensesTree, treeIndex) => {
                                                  const pendingChanges = pendingQuantityChangesMap[treeIndex] || null;
                                                  const subContractId = expensesTree.sub_contract_id;

                                                  // Calculate variance to check if warning should be shown
                                                  let shouldShowWarning = false;
                                                  if (pendingChanges && props.terms?.variation_payments?.length > 0) {
                                                      const subContractTotalPayments = sumVariationGroupAmounts(values.variation_payments?.[subContractId]);
                                                      const treeContractValue = pendingChanges.newTotal;
                                                      const subContractVariance = Math.abs(treeContractValue - subContractTotalPayments);
                                                      shouldShowWarning = subContractVariance > PAYMENT_VARIANCE_TOLERANCE;
                                                  }

                                                  return (
                                                      <div key={treeIndex} className="tw-flex tw-flex-col tw-gap-4">
                                                          {/* Show warning if contract value was reduced AND variance hasn't been resolved */}
                                                          {shouldShowWarning && (
                                                              <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-md tw-p-4">
                                                                  <div className="tw-flex">
                                                                      <div className="tw-flex-shrink-0">
                                                                          <svg
                                                                              className="tw-h-5 tw-w-5 tw-text-yellow-400"
                                                                              viewBox="0 0 20 20"
                                                                              fill="currentColor"
                                                                          >
                                                                              <path
                                                                                  fillRule="evenodd"
                                                                                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                                                  clipRule="evenodd"
                                                                              />
                                                                          </svg>
                                                                      </div>
                                                                      <div className="tw-ml-3">
                                                                          <h3 className="tw-text-sm tw-font-medium tw-text-yellow-800">
                                                                              {$t('Contract Value Reduced')}
                                                                          </h3>
                                                                          <div className="tw-mt-2 tw-text-sm tw-text-yellow-700">
                                                                              <p>
                                                                                  {$t('The contract value has been reduced from')}{' '}
                                                                                  {formatMoneyWithLocale(pendingChanges.originalTotal)} {$t('to')}{' '}
                                                                                  {formatMoneyWithLocale(pendingChanges.newTotal)}.
                                                                              </p>
                                                                              <p className="tw-mt-1">
                                                                                  {$t(
                                                                                      'Please adjust the payment milestones to match the new contract value and save to take effect.',
                                                                                  )}
                                                                              </p>
                                                                          </div>
                                                                      </div>
                                                                  </div>
                                                              </div>
                                                          )}
                                                          <SupplierContractTermExpensesTable
                                                              treeIndex={treeIndex}
                                                              expensesTree={pendingChanges ? pendingChanges.updatedExpenseTree : expensesTree}
                                                              total={pendingChanges ? pendingChanges.newTotal : expensesTree?.total}
                                                              amounts={expensesTree?.amounts}
                                                              venues={expensesTree?.venues}
                                                              updateQuantitiesUrl={props.updateQuantitiesUrl}
                                                              contract={props.contract}
                                                              subContractId={expensesTree?.sub_contract_id}
                                                              onQuantitiesUpdated={handleQuantitiesUpdated}
                                                              onCloseModal={props.onCancel}
                                                              onRefreshContracts={props.onRefreshContracts}
                                                              maxVenuesQuantityTypes={expensesTree?.maxVenuesQuantityTypes}
                                                              onRedirectToPaymentMilestones={(quantityChangeData) =>
                                                                  handleRedirectToPaymentMilestones(quantityChangeData, treeIndex)
                                                              }
                                                          />
                                                      </div>
                                                  );
                                              })
                                            : (() => {
                                                  // Fallback to single expenses_tree for backward compatibility
                                                  // Calculate variance to check if warning should be shown
                                                  let shouldShowWarning = false;
                                                  if (pendingQuantityChanges) {
                                                      const currentTotalPayments =
                                                          values.payments?.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0) || 0;
                                                      const contractValue = pendingQuantityChanges.newTotal;
                                                      const variance = Math.abs(contractValue - currentTotalPayments);
                                                      shouldShowWarning = variance > PAYMENT_VARIANCE_TOLERANCE;
                                                  }

                                                  return (
                                                      <div className="tw-flex tw-flex-col tw-gap-4">
                                                          {/* Show warning if contract value was reduced AND variance hasn't been resolved */}
                                                          {shouldShowWarning && (
                                                              <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-md tw-p-4">
                                                                  <div className="tw-flex">
                                                                      <div className="tw-flex-shrink-0">
                                                                          <svg
                                                                              className="tw-h-5 tw-w-5 tw-text-yellow-400"
                                                                              viewBox="0 0 20 20"
                                                                              fill="currentColor"
                                                                          >
                                                                              <path
                                                                                  fillRule="evenodd"
                                                                                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                                                  clipRule="evenodd"
                                                                              />
                                                                          </svg>
                                                                      </div>
                                                                      <div className="tw-ml-3">
                                                                          <h3 className="tw-text-sm tw-font-medium tw-text-yellow-800">
                                                                              {$t('Contract Value Reduced')}
                                                                          </h3>
                                                                          <div className="tw-mt-2 tw-text-sm tw-text-yellow-700">
                                                                              <p>
                                                                                  {$t('The contract value has been reduced from')}{' '}
                                                                                  {formatMoneyWithLocale(pendingQuantityChanges.originalTotal)} {$t('to')}{' '}
                                                                                  {formatMoneyWithLocale(pendingQuantityChanges.newTotal)}.
                                                                              </p>
                                                                              <p className="tw-mt-1">
                                                                                  {$t(
                                                                                      'Please adjust the payment milestones to match the new contract value and save to take effect.',
                                                                                  )}
                                                                              </p>
                                                                          </div>
                                                                      </div>
                                                                  </div>
                                                              </div>
                                                          )}
                                                          <SupplierContractTermExpensesTable
                                                              expensesTree={
                                                                  pendingQuantityChanges
                                                                      ? pendingQuantityChanges.updatedExpenseTree
                                                                      : props.terms?.expenses_tree
                                                              }
                                                              total={
                                                                  pendingQuantityChanges ? pendingQuantityChanges.newTotal : props.terms?.expenses_tree?.total
                                                              }
                                                              amounts={props.terms?.expenses_tree?.amounts}
                                                              venues={props.terms?.expenses_tree?.venues}
                                                              updateQuantitiesUrl={props.updateQuantitiesUrl}
                                                              contract={props.contract}
                                                              onQuantitiesUpdated={handleQuantitiesUpdated}
                                                              onCloseModal={props.onCancel}
                                                              onRefreshContracts={props.onRefreshContracts}
                                                              maxVenuesQuantityTypes={props.terms?.expenses_tree?.maxVenuesQuantityTypes}
                                                              onRedirectToPaymentMilestones={handleRedirectToPaymentMilestones}
                                                          />
                                                      </div>
                                                  );
                                              })()}

                                        {props.terms?.sponsors_trees?.length > 0
                                            ? props.terms.sponsors_trees.map((sponsorsTree, treeIndex) => {
                                                  return (
                                                      <div key={treeIndex} className="tw-flex tw-flex-col tw-gap-4">
                                                          <ProcurementSponsorsTable
                                                              treeIndex={treeIndex}
                                                              data={sponsorsTree.sponsors}
                                                              total={sponsorsTree.total}
                                                          ></ProcurementSponsorsTable>
                                                      </div>
                                                  );
                                              })
                                            : (() => {
                                                  return (
                                                      <ProcurementSponsorsTable
                                                          data={props.terms?.sponsors_tree?.sponsors}
                                                          total={props.terms?.sponsors_tree?.total}
                                                      ></ProcurementSponsorsTable>
                                                  );
                                              })()}

                                        {props.terms?.revenues_trees?.length > 0
                                            ? props.terms.revenues_trees.map((revenuesTree, treeIndex) => {
                                                  return (
                                                      <div key={treeIndex} className="tw-flex tw-flex-col tw-gap-4">
                                                          <ProcurementRevenuesTable
                                                              treeIndex={treeIndex}
                                                              data={revenuesTree.revenues}
                                                              total={revenuesTree.total}
                                                          ></ProcurementRevenuesTable>
                                                      </div>
                                                  );
                                              })
                                            : (() => {
                                                  return (
                                                      <ProcurementRevenuesTable
                                                          data={props.terms?.revenues_tree?.revenues}
                                                          total={props.terms?.revenues_tree?.total}
                                                      ></ProcurementRevenuesTable>
                                                  );
                                              })()}

                                        {props.terms?.ticketing_trees?.length > 0
                                            ? props.terms.ticketing_trees.map((ticketingTree, treeIndex) => {
                                                  return (
                                                      <div key={treeIndex} className="tw-flex tw-flex-col tw-gap-4">
                                                          <ProcurementTicketingsTable
                                                              treeIndex={treeIndex}
                                                              data={ticketingTree.ticketings}
                                                              total={ticketingTree.total}
                                                          ></ProcurementTicketingsTable>
                                                      </div>
                                                  );
                                              })
                                            : (() => {
                                                  return (
                                                      <ProcurementTicketingsTable
                                                          data={props.terms?.ticketing_tree?.ticketings}
                                                          total={props.terms?.ticketing_tree?.total}
                                                      ></ProcurementTicketingsTable>
                                                  );
                                              })()}

                                        {/* Shown whenever the tab has drawn any group at all, including a single one:
                                            it is the figure the General tab's Value field and the payment milestones
                                            are read against. */}
                                        {renderedValueTotals.length > 0 && (
                                            <div className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border-2 tw-border-grey tw-bg-blue-10 tw-px-4 tw-py-3">
                                                <span className="tw-text-base tw-font-semibold tw-text-charcoal">{$t('Overall Total')}</span>
                                                {/* formatMoneyWithLocale, like every group foot and milestone total, so
                                                    this line reads the same as the figures it sums. */}
                                                <span className="tw-font-mono tw-text-base tw-font-semibold tw-text-charcoal">
                                                    {formatMoneyWithLocale(overallValueTotal)}
                                                </span>
                                            </div>
                                        )}

                                        {props.terms?.contract_reduction_logs && (
                                            <SupplierContractReductionTable data={props.terms?.contract_reduction_logs}></SupplierContractReductionTable>
                                        )}
                                    </div>
                                </div>
                            </TabPanel>
                            <TabPanel>
                                <div className="modal__form-container tw-h-[calc(70vh-2.5rem)] 2xl:tw-h-[calc(50vh-2.5rem)] tw-overflow-y-scroll">
                                    <div className="tw-grid tw-grid-cols-1 tw-gap-8">
                                        {/* Render multiple payment tables for variation_payments */}
                                        {hasMultipleTrees && hasVariationPayments ? (
                                            resourceTrees.map((resourceTree, treeIndex) => {
                                                const subContractId = resourceTree.sub_contract_id;
                                                const pendingChanges = pendingQuantityChangesMap[treeIndex] || null;

                                                const subContractGroup = values.variation_payments?.[subContractId] || { cash: [], vik: [] };
                                                const cashPayments = subContractGroup.cash || [];
                                                const vikPayments = subContractGroup.vik || [];
                                                const cashTotal = cashPayments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
                                                const vikTotal = vikPayments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);

                                                // Calculate contract value for this tree
                                                const treeContractValue = pendingChanges ? pendingChanges.newTotal : resourceTree?.total || 0;

                                                const subContractTotalPayments = cashTotal + vikTotal;

                                                // Combined variance (validation / warning) — unchanged vs contract total
                                                const subContractVariance = treeContractValue - subContractTotalPayments;

                                                // Split contract value target per table from saved contract_term_payments (milestone_type)
                                                const paymentSplit = props.terms?.payment_milestone_totals_by_sub_contract?.[subContractId];
                                                const refCash = parseFloat(paymentSplit?.total_cash) || 0;
                                                const refVik = parseFloat(paymentSplit?.total_vik) || 0;
                                                const shouldShowVikMilestones = refVik > MILESTONE_SPLIT_EPSILON || vikPayments.length > 0;
                                                const refSum = refCash + refVik;
                                                let cashTarget = treeContractValue;
                                                let vikTarget = 0;
                                                if (refSum > MILESTONE_SPLIT_EPSILON) {
                                                    cashTarget = treeContractValue * (refCash / refSum);
                                                    vikTarget = treeContractValue * (refVik / refSum);
                                                }
                                                const cashVariance = cashTarget - cashTotal;
                                                const vikVariance = vikTarget - vikTotal;

                                                return (
                                                    <div key={subContractId || treeIndex} className="tw-flex tw-flex-col tw-gap-6">
                                                        <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                                                            <FormSectionHeading
                                                                heading={treeIndex >= 1 ? $t('Contract Variation') + ' ' + treeIndex : $t('Payment milestones')}
                                                            ></FormSectionHeading>
                                                        </div>

                                                        {/* Show warning if contract value was reduced AND variance hasn't been resolved */}
                                                        {pendingChanges && Math.abs(subContractVariance) > PAYMENT_VARIANCE_TOLERANCE && (
                                                            <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-md tw-p-4">
                                                                <div className="tw-flex">
                                                                    <div className="tw-flex-shrink-0">
                                                                        <svg
                                                                            className="tw-h-5 tw-w-5 tw-text-yellow-400"
                                                                            viewBox="0 0 20 20"
                                                                            fill="currentColor"
                                                                        >
                                                                            <path
                                                                                fillRule="evenodd"
                                                                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                                                clipRule="evenodd"
                                                                            />
                                                                        </svg>
                                                                    </div>
                                                                    <div className="tw-ml-3">
                                                                        <h3 className="tw-text-sm tw-font-medium tw-text-yellow-800">
                                                                            {$t('Contract Value Reduced')}
                                                                        </h3>
                                                                        <div className="tw-mt-2 tw-text-sm tw-text-yellow-700">
                                                                            <p>
                                                                                {$t('The contract value has been reduced from')}{' '}
                                                                                {formatMoneyWithLocale(pendingChanges.originalTotal)} {$t('to')}{' '}
                                                                                {formatMoneyWithLocale(pendingChanges.newTotal)}.
                                                                            </p>
                                                                            <p className="tw-mt-1">
                                                                                {$t(
                                                                                    'Please adjust the payment milestones to match the new contract value and save to take effect.',
                                                                                )}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        <FieldArray name={`variation_payments.${subContractId}.cash`}>
                                                            {(cashHelpers) => (
                                                                <Fragment>
                                                                    <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                                                                        <FormSectionHeading heading={$t('Cash milestones')}></FormSectionHeading>
                                                                        <Button
                                                                            variant="primary"
                                                                            type="button"
                                                                            onClick={() => cashHelpers.push({ ...DEFAULT_PAYMENT_CASH })}
                                                                        >
                                                                            {$t('Add Milestone')}
                                                                        </Button>
                                                                    </div>

                                                                    <div className="inbud-table_wrapper tw-max-h-[calc(70vh-2.5rem)] 2xl:tw-max-h-[calc(50vh-2.5rem)] tw-overflow-y-scroll">
                                                                        <table className="inbud-table">
                                                                            <thead className="tw-sticky tw-top-0 tw-z-10">
                                                                                <tr>
                                                                                    <th>{$t('Description')}</th>
                                                                                    <th>{$t('Date')}</th>
                                                                                    <th>{$t('Amount')}</th>
                                                                                    <th>{$t('Notify')}</th>
                                                                                    <th>{$t('Notify On')}</th>
                                                                                    <th>{$t('Actions')}</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {cashPayments.map((payment, index) => {
                                                                                    const paymentErrors = (errors?.variation_payments?.[subContractId]?.cash ||
                                                                                        [])[index];
                                                                                    const paymentTouched = (touched?.variation_payments?.[subContractId]
                                                                                        ?.cash || [])[index];

                                                                                    return (
                                                                                        <tr key={`cash-${index}`} className="tw-bg-white">
                                                                                            <td>
                                                                                                <TextFormField
                                                                                                    required={false}
                                                                                                    type="text"
                                                                                                    name={`variation_payments.${subContractId}.cash.${index}.description`}
                                                                                                    className="tw-w-full"
                                                                                                />
                                                                                            </td>
                                                                                            <td className="tw-w-[120px]">
                                                                                                <DatePickerField
                                                                                                    name={`variation_payments.${subContractId}.cash.${index}.due_date`}
                                                                                                    className={
                                                                                                        paymentErrors?.due_date && paymentTouched?.due_date
                                                                                                            ? 'tw-border tw-border-red-500 text-input tw-w-full'
                                                                                                            : 'tw-border-0 text-input tw-w-full'
                                                                                                    }
                                                                                                    value={payment.due_date}
                                                                                                    valueFormat="yyyy-MM-dd"
                                                                                                    dateFormat="yyyy-MM-dd"
                                                                                                    onChange={setFieldValue}
                                                                                                    min={props.eventStartDate}
                                                                                                    max={props.eventEndDate}
                                                                                                />
                                                                                                {paymentErrors?.due_date && paymentTouched?.due_date && (
                                                                                                    <div className="tw-text-red-500 tw-text-xs tw-mt-1">
                                                                                                        {paymentErrors?.due_date}
                                                                                                    </div>
                                                                                                )}
                                                                                            </td>
                                                                                            <td className="tw-w-64">
                                                                                                <TextFormField
                                                                                                    hideLabel={true}
                                                                                                    required={true}
                                                                                                    name={`variation_payments.${subContractId}.cash.${index}.amount`}
                                                                                                    formatted
                                                                                                    thousandSeparator={true}
                                                                                                    inputMode="numeric"
                                                                                                    decimalScale={2}
                                                                                                    className="tw-w-full text-right"
                                                                                                />
                                                                                            </td>
                                                                                            <td>
                                                                                                <ToggleFormField
                                                                                                    type="checkbox"
                                                                                                    name={`variation_payments.${subContractId}.cash.${index}.set_notification`}
                                                                                                    id={`variation_payments.${subContractId}.cash.${index}_set_notification`}
                                                                                                />
                                                                                            </td>
                                                                                            <td className="tw-w-[120px]">
                                                                                                {payment.set_notification && (
                                                                                                    <DatePickerField
                                                                                                        name={`variation_payments.${subContractId}.cash.${index}.notify_at`}
                                                                                                        className={
                                                                                                            paymentErrors?.notify_at &&
                                                                                                            paymentTouched?.notify_at
                                                                                                                ? 'tw-border tw-border-red-500 text-input tw-w-full'
                                                                                                                : 'tw-border-0 text-input tw-w-full'
                                                                                                        }
                                                                                                        value={payment.notify_at}
                                                                                                        valueFormat="yyyy-MM-dd"
                                                                                                        dateFormat="yyyy-MM-dd"
                                                                                                        onChange={setFieldValue}
                                                                                                    />
                                                                                                )}
                                                                                                {paymentErrors?.notify_at && paymentTouched?.notify_at && (
                                                                                                    <div className="tw-text-red-500 tw-text-xs tw-mt-1">
                                                                                                        {paymentErrors?.notify_at}
                                                                                                    </div>
                                                                                                )}
                                                                                            </td>
                                                                                            <td>
                                                                                                <div className="tw-flex tw-justify-center tw-items-center">
                                                                                                    <IconButton
                                                                                                        variant="danger"
                                                                                                        size="sm"
                                                                                                        ghost
                                                                                                        className="focus-visible:tw-ring-2 focus-visible:tw-ring-red focus-visible:tw-ring-offset-1"
                                                                                                        title={$t('Remove milestone')}
                                                                                                        aria-label={$t('Remove milestone')}
                                                                                                        onClick={() => cashHelpers.remove(index)}
                                                                                                    >
                                                                                                        <TrashIcon
                                                                                                            className="tw-w-3 tw-h-3"
                                                                                                            aria-hidden="true"
                                                                                                        />
                                                                                                    </IconButton>
                                                                                                </div>
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}

                                                                                <tr className="!tw-bg-white">
                                                                                    <td className="!tw-px-4 tw-font-semibold tw-text-charcoal">
                                                                                        {$t('Milestones total')}
                                                                                    </td>
                                                                                    <td></td>
                                                                                    <td className="!tw-px-4 tw-font-semibold tw-text-charcoal tw-text-end">
                                                                                        {formatFigure(cashTotal, formatMoneyWithLocale, { precision: 3 })}
                                                                                    </td>
                                                                                    <td colSpan="3"></td>
                                                                                </tr>

                                                                                <tr className="!tw-bg-white">
                                                                                    <td className="tw-font-semibold !tw-px-4 tw-text-charcoal-75">
                                                                                        {$t('Variance to contract value')}
                                                                                    </td>
                                                                                    <td></td>
                                                                                    <td className="!tw-px-4 tw-font-semibold tw-text-end">
                                                                                        <MilestoneVariance variance={cashVariance} />
                                                                                    </td>
                                                                                    <td colSpan="3"></td>
                                                                                </tr>
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </Fragment>
                                                            )}
                                                        </FieldArray>

                                                        {shouldShowVikMilestones && (
                                                            <FieldArray name={`variation_payments.${subContractId}.vik`}>
                                                                {(vikHelpers) => (
                                                                    <Fragment>
                                                                        <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                                                                            <FormSectionHeading heading={$t('VIK milestones')}></FormSectionHeading>
                                                                            <Button
                                                                                variant="primary"
                                                                                type="button"
                                                                                onClick={() => vikHelpers.push({ ...DEFAULT_PAYMENT_VIK })}
                                                                            >
                                                                                {$t('Add Milestone')}
                                                                            </Button>
                                                                        </div>

                                                                        <div className="inbud-table_wrapper tw-max-h-[calc(70vh-2.5rem)] 2xl:tw-max-h-[calc(50vh-2.5rem)] tw-overflow-y-scroll">
                                                                            <table className="inbud-table">
                                                                                <thead className="tw-sticky tw-top-0 tw-z-10">
                                                                                    <tr>
                                                                                        <th>{$t('Description')}</th>
                                                                                        <th>{$t('Date')}</th>
                                                                                        <th>{$t('Amount')}</th>
                                                                                        <th>{$t('Notify')}</th>
                                                                                        <th>{$t('Notify On')}</th>
                                                                                        <th>{$t('Actions')}</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {vikPayments.map((payment, index) => {
                                                                                        const paymentErrors = (errors?.variation_payments?.[subContractId]
                                                                                            ?.vik || [])[index];
                                                                                        const paymentTouched = (touched?.variation_payments?.[subContractId]
                                                                                            ?.vik || [])[index];

                                                                                        return (
                                                                                            <tr key={`vik-${index}`} className="tw-bg-white">
                                                                                                <td>
                                                                                                    <TextFormField
                                                                                                        required={false}
                                                                                                        type="text"
                                                                                                        name={`variation_payments.${subContractId}.vik.${index}.description`}
                                                                                                        className="tw-w-full"
                                                                                                    />
                                                                                                </td>
                                                                                                <td className="tw-w-[120px]">
                                                                                                    <DatePickerField
                                                                                                        name={`variation_payments.${subContractId}.vik.${index}.due_date`}
                                                                                                        className={
                                                                                                            paymentErrors?.due_date && paymentTouched?.due_date
                                                                                                                ? 'tw-border tw-border-red-500 text-input tw-w-full'
                                                                                                                : 'tw-border-0 text-input tw-w-full'
                                                                                                        }
                                                                                                        value={payment.due_date}
                                                                                                        valueFormat="yyyy-MM-dd"
                                                                                                        dateFormat="yyyy-MM-dd"
                                                                                                        onChange={setFieldValue}
                                                                                                        min={props.eventStartDate}
                                                                                                        max={props.eventEndDate}
                                                                                                    />
                                                                                                    {paymentErrors?.due_date && paymentTouched?.due_date && (
                                                                                                        <div className="tw-text-red-500 tw-text-xs tw-mt-1">
                                                                                                            {paymentErrors?.due_date}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </td>
                                                                                                <td className="tw-w-64">
                                                                                                    <TextFormField
                                                                                                        hideLabel={true}
                                                                                                        required={true}
                                                                                                        name={`variation_payments.${subContractId}.vik.${index}.amount`}
                                                                                                        formatted
                                                                                                        thousandSeparator={true}
                                                                                                        inputMode="numeric"
                                                                                                        decimalScale={2}
                                                                                                        className="tw-w-full text-right"
                                                                                                    />
                                                                                                </td>
                                                                                                <td>
                                                                                                    <ToggleFormField
                                                                                                        type="checkbox"
                                                                                                        name={`variation_payments.${subContractId}.vik.${index}.set_notification`}
                                                                                                        id={`variation_payments.${subContractId}.vik.${index}_set_notification`}
                                                                                                    />
                                                                                                </td>
                                                                                                <td className="tw-w-[120px]">
                                                                                                    {payment.set_notification && (
                                                                                                        <DatePickerField
                                                                                                            name={`variation_payments.${subContractId}.vik.${index}.notify_at`}
                                                                                                            className={
                                                                                                                paymentErrors?.notify_at &&
                                                                                                                paymentTouched?.notify_at
                                                                                                                    ? 'tw-border tw-border-red-500 text-input tw-w-full'
                                                                                                                    : 'tw-border-0 text-input tw-w-full'
                                                                                                            }
                                                                                                            value={payment.notify_at}
                                                                                                            valueFormat="yyyy-MM-dd"
                                                                                                            dateFormat="yyyy-MM-dd"
                                                                                                            onChange={setFieldValue}
                                                                                                        />
                                                                                                    )}
                                                                                                    {paymentErrors?.notify_at && paymentTouched?.notify_at && (
                                                                                                        <div className="tw-text-red-500 tw-text-xs tw-mt-1">
                                                                                                            {paymentErrors?.notify_at}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </td>
                                                                                                <td>
                                                                                                    <div className="tw-flex tw-justify-center tw-items-center">
                                                                                                        <IconButton
                                                                                                            variant="danger"
                                                                                                            size="sm"
                                                                                                            ghost
                                                                                                            className="focus-visible:tw-ring-2 focus-visible:tw-ring-red focus-visible:tw-ring-offset-1"
                                                                                                            title={$t('Remove milestone')}
                                                                                                            aria-label={$t('Remove milestone')}
                                                                                                            onClick={() => vikHelpers.remove(index)}
                                                                                                        >
                                                                                                            <TrashIcon
                                                                                                                className="tw-w-3 tw-h-3"
                                                                                                                aria-hidden="true"
                                                                                                            />
                                                                                                        </IconButton>
                                                                                                    </div>
                                                                                                </td>
                                                                                            </tr>
                                                                                        );
                                                                                    })}

                                                                                    <tr className="!tw-bg-white">
                                                                                        <td className="!tw-px-4 tw-font-semibold tw-text-charcoal">
                                                                                            {$t('Milestones total')}
                                                                                        </td>
                                                                                        <td></td>
                                                                                        <td className="!tw-px-4 tw-font-semibold tw-text-charcoal tw-text-end">
                                                                                            {formatFigure(vikTotal, formatMoneyWithLocale, { precision: 3 })}
                                                                                        </td>
                                                                                        <td colSpan="3"></td>
                                                                                    </tr>

                                                                                    <tr className="!tw-bg-white">
                                                                                        <td className="tw-font-semibold !tw-px-4 tw-text-charcoal-75">
                                                                                            {$t('Variance to contract value')}
                                                                                        </td>
                                                                                        <td></td>
                                                                                        <td className="!tw-px-4 tw-font-semibold tw-text-end">
                                                                                            <MilestoneVariance variance={vikVariance} />
                                                                                        </td>
                                                                                        <td colSpan="3"></td>
                                                                                    </tr>
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </Fragment>
                                                                )}
                                                            </FieldArray>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            /* Fallback to single payments table for backward compatibility */
                                            <FieldArray name="payments">
                                                {(arrayHelpers) => {
                                                    return (
                                                        <Fragment>
                                                            <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                                                                <FormSectionHeading heading={$t('Payment milestones')}></FormSectionHeading>
                                                                <Button variant="primary" type="button" onClick={() => arrayHelpers.push(DEFAULT_PAYMENT)}>
                                                                    {$t('Add Milestone')}
                                                                </Button>
                                                            </div>

                                                            {!!values.payments?.length && (
                                                                <div className="inbud-table_wrapper tw-max-h-[calc(70vh-2.5rem)] 2xl:tw-max-h-[calc(50vh-2.5rem)] tw-overflow-y-scroll">
                                                                    <table className="inbud-table">
                                                                        <thead className="tw-sticky tw-top-0 tw-z-10">
                                                                            <tr>
                                                                                <th>{$t('Description')}</th>
                                                                                <th>{$t('Date')}</th>
                                                                                <th>{$t('Amount')}</th>
                                                                                <th>{$t('Notify')}</th>
                                                                                <th>{$t('Notify On')}</th>
                                                                                <th>{$t('Actions')}</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {values.payments.map((payment, index) => {
                                                                                const paymentErrors = (errors?.payments || [])[index];
                                                                                const paymentTouched = (touched?.payments || [])[index];

                                                                                return (
                                                                                    <tr key={index} className="tw-bg-white">
                                                                                        <td>
                                                                                            <TextFormField
                                                                                                required={false}
                                                                                                type="text"
                                                                                                name={`payments.${index}.description`}
                                                                                                className="tw-w-full"
                                                                                            />
                                                                                        </td>
                                                                                        <td className="tw-w-[120px]">
                                                                                            <DatePickerField
                                                                                                name={`payments.${index}.due_date`}
                                                                                                className={
                                                                                                    paymentErrors?.due_date && paymentTouched?.due_date
                                                                                                        ? 'tw-border tw-border-red-500 text-input tw-w-full'
                                                                                                        : 'tw-border-0 text-input tw-w-full'
                                                                                                }
                                                                                                value={payment.due_date}
                                                                                                valueFormat="yyyy-MM-dd"
                                                                                                dateFormat="yyyy-MM-dd"
                                                                                                onChange={setFieldValue}
                                                                                                min={props.eventStartDate}
                                                                                                max={props.eventEndDate}
                                                                                            />
                                                                                            {paymentErrors?.due_date && paymentTouched?.due_date && (
                                                                                                <div className="tw-text-red-500 tw-text-xs tw-mt-1">
                                                                                                    {paymentErrors?.due_date}
                                                                                                </div>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="tw-w-64">
                                                                                            <TextFormField
                                                                                                hideLabel={true}
                                                                                                required={true}
                                                                                                name={`payments.${index}.amount`}
                                                                                                formatted
                                                                                                thousandSeparator={true}
                                                                                                inputMode="numeric"
                                                                                                decimalScale={2}
                                                                                                className="tw-w-full text-right"
                                                                                            />
                                                                                        </td>
                                                                                        <td>
                                                                                            <ToggleFormField
                                                                                                type="checkbox"
                                                                                                name={`payments.${index}.set_notification`}
                                                                                                id={`payments.${index}_set_notification`}
                                                                                            />
                                                                                        </td>
                                                                                        <td className="tw-w-[120px]">
                                                                                            {payment.set_notification && (
                                                                                                <DatePickerField
                                                                                                    name={`payments.${index}.notify_at`}
                                                                                                    className={
                                                                                                        paymentErrors?.notify_at && paymentTouched?.notify_at
                                                                                                            ? 'tw-border tw-border-red-500 text-input tw-w-full'
                                                                                                            : 'tw-border-0 text-input tw-w-full'
                                                                                                    }
                                                                                                    value={payment.notify_at}
                                                                                                    valueFormat="yyyy-MM-dd"
                                                                                                    dateFormat="yyyy-MM-dd"
                                                                                                    onChange={setFieldValue}
                                                                                                />
                                                                                            )}
                                                                                            {paymentErrors?.notify_at && paymentTouched?.notify_at && (
                                                                                                <div className="tw-text-red-500 tw-text-xs tw-mt-1">
                                                                                                    {paymentErrors?.notify_at}
                                                                                                </div>
                                                                                            )}
                                                                                        </td>
                                                                                        <td>
                                                                                            <div className="tw-flex tw-justify-center tw-items-center">
                                                                                                <IconButton
                                                                                                    variant="danger"
                                                                                                    size="sm"
                                                                                                    ghost
                                                                                                    className="focus-visible:tw-ring-2 focus-visible:tw-ring-red focus-visible:tw-ring-offset-1"
                                                                                                    title={$t('Remove milestone')}
                                                                                                    aria-label={$t('Remove milestone')}
                                                                                                    onClick={() => arrayHelpers.remove(index)}
                                                                                                >
                                                                                                    <TrashIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                                                                                                </IconButton>
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })}

                                                                            <tr className="!tw-bg-white">
                                                                                <td className="!tw-px-4 tw-font-semibold tw-text-charcoal">
                                                                                    {$t('Milestones total')}
                                                                                </td>
                                                                                <td></td>
                                                                                <td className="!tw-px-4 tw-font-semibold tw-text-charcoal tw-text-end">
                                                                                    {formatFigure(currentTotalPayments, formatMoneyWithLocale, {
                                                                                        precision: 3,
                                                                                    })}
                                                                                </td>
                                                                                <td colSpan="3"></td>
                                                                            </tr>

                                                                            <tr className="!tw-bg-white">
                                                                                <td className="tw-font-semibold !tw-px-4 tw-text-charcoal-75">
                                                                                    {$t('Variance to contract value')}
                                                                                </td>
                                                                                <td></td>
                                                                                <td className="!tw-px-4 tw-font-semibold tw-text-end">
                                                                                    <MilestoneVariance variance={currentVariance} />
                                                                                </td>
                                                                                <td colSpan="3"></td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            )}
                                                        </Fragment>
                                                    );
                                                }}
                                            </FieldArray>
                                        )}
                                    </div>
                                </div>
                            </TabPanel>
                            <TabPanel>
                                <div className="modal__form-container tw-h-[calc(70vh-2.5rem)] 2xl:tw-h-[calc(50vh-2.5rem)] tw-overflow-y-scroll">
                                    <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                                        <FieldArray name="milestones">
                                            {(arrayHelpers) => (
                                                <Fragment>
                                                    <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                                                        <FormSectionHeading heading={$t('Other milestones')}></FormSectionHeading>
                                                        {!!values.milestones?.length && (
                                                            <Button variant="primary" onClick={() => arrayHelpers.push(DEFAULT_MILESTONE)}>
                                                                {$t('Add a milestone')}
                                                            </Button>
                                                        )}
                                                    </div>
                                                    {values.milestones?.length ? (
                                                        values.milestones.map((milestone, index) => {
                                                            const milestoneErrors = (errors?.milestones || [])[index];
                                                            const milestoneTouched = (touched?.milestones || [])[index];

                                                            return (
                                                                <Fragment key={index}>
                                                                    <MilestoneField
                                                                        name={`milestones.${index}`}
                                                                        value={milestone}
                                                                        errors={milestoneErrors}
                                                                        touched={milestoneTouched}
                                                                        setFieldValue={setFieldValue}
                                                                        actions={
                                                                            <IconButton
                                                                                variant="danger"
                                                                                size="sm"
                                                                                ghost
                                                                                className="focus-visible:tw-ring-2 focus-visible:tw-ring-red focus-visible:tw-ring-offset-1"
                                                                                title={$t('Remove milestone')}
                                                                                aria-label={$t('Remove milestone')}
                                                                                onClick={() => arrayHelpers.remove(index)}
                                                                            >
                                                                                <TrashIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                                                                            </IconButton>
                                                                        }
                                                                    />
                                                                    {index < values.milestones?.length - 1 && <hr className="tw-my-1" />}
                                                                </Fragment>
                                                            );
                                                        })
                                                    ) : (
                                                        <EmptyState
                                                            className="tw-py-10"
                                                            icon={HourglassIcon}
                                                            heading={$t('No other milestones yet')}
                                                            body={$t("Track delivery dates, reviews, or obligations that aren't tied to payment.")}
                                                            action={
                                                                <Button variant="primary" onClick={() => arrayHelpers.push(DEFAULT_MILESTONE)}>
                                                                    <CirclePlusIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                                                                    {$t('Add a milestone')}
                                                                </Button>
                                                            }
                                                        />
                                                    )}
                                                </Fragment>
                                            )}
                                        </FieldArray>
                                    </div>
                                </div>
                            </TabPanel>
                            <TabPanel>
                                <div className="modal__form-container tw-h-[calc(70vh-2.5rem)] 2xl:tw-h-[calc(50vh-2.5rem)] tw-overflow-y-scroll">
                                    <SupplierContractTermGoodsReceivedTable
                                        expensesTree={props.terms?.expenses_tree}
                                        expensesTrees={props.terms?.expenses_trees}
                                        ownerName={props.terms?.owner?.name}
                                        canRecord={!!props.terms?.can_record_goods_receipt}
                                        recorded={goodsReceiptsRecorded}
                                        onRecorded={(id, entry) => setGoodsReceiptsRecorded((state) => ({ ...state, [id]: entry }))}
                                        /* A receipt posts to its own nested resource, which hangs off the term's own
                                           URL — the only one the hosting Blade views already inject. */
                                        storeUrl={`${props.updateUrl?.replace('CONTRACT_ID', props.contract?.id).replace('TERM_ID', props.terms?.id)}/goods-receipts`}
                                    />
                                </div>
                            </TabPanel>
                            <TabPanel>
                                <div className="modal__form-container tw-h-[calc(70vh-2.5rem)] 2xl:tw-h-[calc(50vh-2.5rem)] tw-overflow-y-scroll">
                                    <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                                        <FieldArray name="notes">
                                            {(arrayHelpers) => (
                                                <Fragment>
                                                    <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                                                        <FormSectionHeading heading={$t('Supplier Notes')}></FormSectionHeading>
                                                        {!!values.notes?.length && (
                                                            <Button variant="primary" onClick={() => arrayHelpers.push(DEFAULT_NOTE)}>
                                                                {$t('Add note')}
                                                            </Button>
                                                        )}
                                                    </div>
                                                    {values.notes?.length ? (
                                                        values.notes.map((note, index) => {
                                                            const noteErrors = (errors?.notes || [])[index];
                                                            const noteTouched = (touched?.notes || [])[index];

                                                            return (
                                                                <Fragment key={index}>
                                                                    <NoteField
                                                                        name={`notes.${index}`}
                                                                        value={note}
                                                                        errors={noteErrors}
                                                                        touched={noteTouched}
                                                                        setFieldValue={setFieldValue}
                                                                        actions={
                                                                            <IconButton
                                                                                variant="danger"
                                                                                size="sm"
                                                                                ghost
                                                                                className="focus-visible:tw-ring-2 focus-visible:tw-ring-red focus-visible:tw-ring-offset-1"
                                                                                title={$t('Remove note')}
                                                                                aria-label={$t('Remove note')}
                                                                                onClick={() => arrayHelpers.remove(index)}
                                                                            >
                                                                                <TrashIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                                                                            </IconButton>
                                                                        }
                                                                    />
                                                                    {index < values.notes?.length - 1 && <hr className="tw-my-1" />}
                                                                </Fragment>
                                                            );
                                                        })
                                                    ) : (
                                                        <EmptyState
                                                            className="tw-py-10"
                                                            icon={PenSimpleIcon}
                                                            heading={$t('No notes yet')}
                                                            body={$t('Keep a running record of conversations, issues, or decisions for this supplier.')}
                                                            action={
                                                                <Button variant="primary" onClick={() => arrayHelpers.push(DEFAULT_NOTE)}>
                                                                    <CirclePlusIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                                                                    {$t('Add note')}
                                                                </Button>
                                                            }
                                                        />
                                                    )}
                                                </Fragment>
                                            )}
                                        </FieldArray>
                                    </div>
                                </div>
                            </TabPanel>
                        </Tabs>

                        {/* Hide ModalFooter on Value tab (index 1) */}
                        {tabIndex !== 1 && (
                            <ModalFooter
                                cancelButtonText={$t('Cancel')}
                                primaryButtonText={$t('Save')}
                                submitButtonVariant="primary"
                                onCancel={props.onCancel}
                                handleSubmit={handleSaveClick}
                                isSubmitting={isSubmitting}
                                primaryButtonDisabled={primaryButtonDisabled}
                            ></ModalFooter>
                        )}
                        {tabIndex === 1 && <div className="modal__footer tw-justify-between tw-h-8"></div>}
                    </>
                );
            }}
        </Formik>
    );
}

function OwnerSelector(props) {
    const { users, errors, touched, setFieldValue } = props;
    const [selectUser, setSelectUser] = useState(true);

    useEffect(() => {
        if (selectUser) {
            setFieldValue('owner_name', '');
            setFieldValue('owner_email', '');
        } else {
            setFieldValue('owner_id', '');
        }
    }, [selectUser]);

    if (selectUser) {
        return (
            <>
                <div className="form-group">
                    <label htmlFor="owner_id">{$t('Owner')}</label>
                    <Field
                        as="select"
                        className={errors.owner_id && touched.owner_id ? 'form-control is-invalid border border-danger' : 'form-control'}
                        name="owner_id"
                    >
                        <option>{$t('Choose User')}</option>
                        {users.map((user) => {
                            return (
                                <option key={user.id} value={user.id}>
                                    {user.name} - {user.email}
                                </option>
                            );
                        })}
                    </Field>
                    {errors.owner_id && touched.owner_id && <div className="text-danger">{errors.owner_id}</div>}
                </div>
                <Button small onClick={() => setSelectUser(false)}>
                    {$t('Add manually')}
                </Button>
            </>
        );
    }

    return (
        <>
            <div className="form-group">
                <label htmlFor="owner_name">{$t('Owner name')}</label>
                <Field
                    type="text"
                    className={errors.owner_name && touched.owner_name ? 'form-control is-invalid border border-danger' : 'form-control'}
                    name="owner_name"
                />
                {errors.owner_name && touched.owner_name && <div className="text-danger">{errors.owner_name}</div>}
            </div>
            <div className="form-group">
                <label htmlFor="owner_email">{$t('Owner email')}</label>
                <Field
                    type="text"
                    className={errors.owner_email && touched.owner_email ? 'form-control is-invalid border border-danger' : 'form-control'}
                    name="owner_email"
                />
                {errors.owner_email && touched.owner_email && <div className="text-danger">{errors.owner_email}</div>}
            </div>
            <Button small onClick={() => setSelectUser(true)}>
                {$t('Select from existing users')}
            </Button>
        </>
    );
}

// The variance cell of a milestone table's summary. Within tolerance it says so in words
// rather than printing a rounded zero the reader has to interpret; outside it, the signed
// shortfall stays red.
function MilestoneVariance({ variance }) {
    if (Math.abs(variance) <= PAYMENT_VARIANCE_TOLERANCE) {
        return (
            <span className="tw-inline-flex tw-items-center tw-justify-end tw-gap-x-1.5 tw-text-green">
                <CheckIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                {$t('Balanced')}
            </span>
        );
    }

    return <span className="tw-text-red-500">{formatMoneyWithLocale(variance)}</span>;
}

function MilestoneField(props) {
    const { name, value, errors, touched, actions, setFieldValue } = props;

    return (
        <div className="tw-grid tw-grid-cols-12 tw-gap-4">
            <div className="tw-col-span-12 md:tw-col-span-7">
                <TextareaFormField label={$t('Description')} required={false} as="textarea" type="text" name={`${name}.description`}></TextareaFormField>
            </div>
            <div className="tw-grid tw-grid-cols-subgrid tw-gap-4 tw-col-span-12 md:tw-col-span-4">
                <div className="tw-col-span-12 md:tw-col-span-4">
                    <ToggleFormField
                        label={$t('Set Notification')}
                        type="checkbox"
                        name={`${name}.set_notification`}
                        id={`${name}_set_notification`}
                    ></ToggleFormField>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-4">
                    {value.set_notification && (
                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                            <label className="text-input__label">{$t('Notify At')} *</label>
                            <DatePickerField
                                name={`${name}.notify_at`}
                                className={errors?.notify_at && touched?.notify_at ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'}
                                value={value.notify_at}
                                valueFormat="yyyy-MM-dd"
                                dateFormat="yyyy-MM-dd"
                                onChange={setFieldValue}
                            />
                            {errors?.notify_at && touched?.notify_at && <div className="tw-text-red-500 tw-text-sm">{errors?.notify_at}</div>}
                        </div>
                    )}
                </div>
            </div>
            <div className="tw-col-span-12 md:tw-col-span-1 tw-flex tw-flex-col tw-mt-6 tw-items-center">{actions}</div>
            <div className="tw-grid tw-grid-cols-subgrid tw-gap-4 tw-col-span-12">
                <div className="tw-col-span-12 md:tw-col-span-4">
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">{$t('Due Date')} *</label>
                        <DatePickerField
                            name={`${name}.due_date`}
                            className={errors?.due_date && touched?.due_date ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'}
                            value={value.due_date}
                            valueFormat="yyyy-MM-dd"
                            dateFormat="yyyy-MM-dd"
                            onChange={setFieldValue}
                        />
                        {errors?.due_date && touched?.due_date && <div className="tw-text-red-500 tw-text-sm">{errors?.due_date}</div>}
                    </div>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-4">
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">{$t('Forecast due date')}</label>
                        <DatePickerField
                            name={`${name}.forecast_due_date`}
                            className={
                                errors?.forecast_due_date && touched?.forecast_due_date ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'
                            }
                            value={value.forecast_due_date}
                            valueFormat="yyyy-MM-dd"
                            dateFormat="yyyy-MM-dd"
                            onChange={setFieldValue}
                        />
                        {errors?.forecast_due_date && touched?.forecast_due_date && (
                            <div className="tw-text-red-500 tw-text-sm">{errors?.forecast_due_date}</div>
                        )}
                    </div>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-4">
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">{$t('Completed date')}</label>
                        <DatePickerField
                            name={`${name}.completed_date`}
                            className={errors?.completed_date && touched?.completed_date ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'}
                            value={value.completed_date}
                            valueFormat="yyyy-MM-dd"
                            dateFormat="yyyy-MM-dd"
                            onChange={setFieldValue}
                        />
                        {errors?.completed_date && touched?.completed_date && <div className="tw-text-red-500 tw-text-sm">{errors?.completed_date}</div>}
                    </div>
                </div>
            </div>
        </div>
    );
}

function PenaltyField(props) {
    const { name, errors, touched, actions } = props;

    return (
        <div className="tw-grid tw-grid-cols-12 tw-gap-4">
            <div className="tw-col-span-12 md:tw-col-span-11">
                <TextFormField label={$t('Description')} required={true} type="text" name={`${name}.description`}></TextFormField>
            </div>
            <div className="tw-col-span-12 md:tw-col-span-1 tw-flex tw-flex-col tw-mt-6 tw-items-center">{actions}</div>
        </div>
    );
}

function PartyField(props) {
    const { name, errors, touched, actions } = props;

    return (
        <div className="tw-grid tw-grid-cols-12 tw-gap-4">
            <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-col-span-12 md:tw-col-span-11">
                <div className="tw-col-span-12 md:tw-col-span-4">
                    <TextFormField label={$t('Name')} required={true} type="text" name={`${name}.name`}></TextFormField>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-4">
                    <TextFormField label={$t('Position')} required={true} type="text" name={`${name}.position`}></TextFormField>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-4">
                    <TextFormField label={$t('Role')} required={true} type="text" name={`${name}.role`}></TextFormField>
                </div>
            </div>
            <div className="tw-col-span-12 md:tw-col-span-1 tw-flex tw-flex-col tw-mt-6 tw-items-center">{actions}</div>
        </div>
    );
}

function NoteField(props) {
    const { name, errors, touched, actions } = props;

    return (
        <div className="tw-grid tw-grid-cols-12 tw-gap-4">
            <div className="tw-col-span-12 md:tw-col-span-11">
                <TextareaFormField label={$t('Content')} required={true} as="textarea" type="text" name={`${name}.content`}></TextareaFormField>
            </div>
            <div className="tw-col-span-12 md:tw-col-span-1 tw-flex tw-flex-col tw-mt-6 tw-items-center">{actions}</div>
        </div>
    );
}

function Attachments(props) {
    const { attachments, onDelete, onDownload } = props;

    return (
        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
            {attachments.map((attachment) => (
                <div key={attachment.id} className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                    <a href="#" onClick={(e) => onDownload(e, attachment)}>
                        {attachment.file_name}
                    </a>
                    {/* Icon only, like every other row-removal control in the modal. The label it drops
                        is not the last chance to read what is about to go: the handler raises a
                        confirmation naming the file before anything is deleted. */}
                    <IconButton
                        variant="danger"
                        size="sm"
                        ghost
                        className="focus-visible:tw-ring-2 focus-visible:tw-ring-red focus-visible:tw-ring-offset-1"
                        title={$t('Delete attachment')}
                        aria-label={$t('Delete attachment')}
                        onClick={() => onDelete(attachment)}
                    >
                        <TrashIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                    </IconButton>
                </div>
            ))}
        </div>
    );
}
